import { useEffect, useId, useRef } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { FilterBar } from '../../components/FilterBar';
import TimelineItems from './components/TimelineItems';
import TimelinePagingHandler from './components/TimelinePagingHandler';
import useTimelineEntries from './internalHooks/useTimelineEntries';
import useTimelineRecords from './internalHooks/useTimelineRecords';
import type { TimelineConfiguredProps } from './types/configTypes';
import { TimelineSourceRecord } from './types/types';
import { mergeSx } from 'framework/utils';
import { useTimelineEditor } from './internalHooks/useTimelineEditor';

const listSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const Timeline = <Record extends TimelineSourceRecord>(props: TimelineConfiguredProps<Record>) => {
  const {
    entryId,
    emptyStateText = 'No activity yet.',
    filterBarProps,
    isEntryViewMode = false,
    isLoading = false,
    isLoadingMore = false,
    isLoadingPrevious = false,
    hasPrevious = false,
    hasMore = false,
    loadPrevious,
    loadMore,
    error,
    retry,
    sx,
  } = props;
  const localEditor = useTimelineEditor();
  const editor = props.editor ?? localEditor;
  const { items: timelineItems, addForm, editForm } = useTimelineRecords(props, editor);
  const { entries, setPinned } = useTimelineEntries(timelineItems);
  const instanceId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const scrolledEntryIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isEntryViewMode || entryId === undefined) {
      scrolledEntryIdRef.current = null;
      return;
    }

    const entryKey = String(entryId);
    if (isLoading || scrolledEntryIdRef.current === entryKey) return;

    const entryElement = Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>('[data-timeline-entry-id]') ?? [],
    ).find(element => element.id === `timeline-${instanceId}-entry-${entryKey}`);
    if (!entryElement) return;

    entryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    scrolledEntryIdRef.current = entryKey;
  }, [entryId, instanceId, isEntryViewMode, isLoading, timelineItems]);

  return (
    <Box ref={rootRef} sx={mergeSx(listSx, sx)}>
      {filterBarProps && <FilterBar {...filterBarProps} />}
      <TimelinePagingHandler
        error={error}
        retry={retry}
        hasPrevious={hasPrevious}
        hasMore={hasMore}
        isInitialLoading={isLoading && !addForm}
        isLoadingMore={isLoadingMore}
        isLoadingPrevious={isLoadingPrevious}
        itemCount={entries.length}
        loadPrevious={loadPrevious}
        loadMore={loadMore}
      >
        <TimelineItems
          instanceId={instanceId}
          entries={entries}
          RecordAuthor={props.config.RecordAuthor}
          emptyStateText={emptyStateText}
          addForm={addForm}
          editForm={editForm}
          highlightedEntryId={entryId}
          onPinned={setPinned}
        />
      </TimelinePagingHandler>
    </Box>
  );
};

export default Timeline;
